/* eslint-disable react/forbid-component-props */
import { Linkable } from '@/components/blocks/Linkable';
import { MyRecentOrderStatus } from '@/components/content/Header/parts/HDMMyRecentOrders/MyRecentOrderStatus';
import { TextLine } from '@/components/content/Header/parts/HDMMyRecentOrders/TextLine';
import { extraLinkSX } from '@/components/content/Header/styles/account/panel';
import { EMPTY_STRING } from '@/data/constants/order';
import { useLocalization } from '@/data/Localization';
import { formatPrice } from '@/utils/formatPrice';
import { Box, Divider, Typography } from '@mui/material';
import { OrderItems, RecentOrdersResponse } from 'integration/generated/custom/data-contracts';
import { OrderOrderSummaryItem } from 'integration/generated/transactions/data-contracts';
import {
	CDN_BASE_URL,
	LOCALE_ES_MX,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';
import { CardImage } from '@/components/content/Header/parts/HDMImageCard';
import { RecentOrdersDeliveryDate } from './RecentOrderDeliveryDate';

interface MappedProductType extends OrderOrderSummaryItem {
	products: OrderItems[];
}

const MAX_NUMBER_ITEMS_SHOW = 5;
const MAX_NUMBER_ITEMS_SHOW_INDEX = 6;
const MAX_NUMBER_ORDERS_SHOW_INDEX = 5;

export const MyRecentOrdersList = ({ Order, OrderItems }: RecentOrdersResponse) => {
	const routes = useLocalization('Routes');
	const HeaderMyRecentOrdersLabels = useLocalization('HeaderMyRecentOrderPanel');
	const mappedOrders = Order?.map((order: OrderOrderSummaryItem) => ({
		...order,
		products: OrderItems?.filter((orderItem: OrderItems) => {
			if (orderItem.orderId === order.orderId) return orderItem.items;
		}),
	}));

	return (
		<>
			{mappedOrders?.slice(0, MAX_NUMBER_ORDERS_SHOW_INDEX).map((mappedOrder) => {
				const partNumbers = Array.from(
					new Set(
						(mappedOrder as MappedProductType).products.flatMap((p) =>
							p.items.map((i) => i.partNumber)
						)
					)
				);

				const itemsToShow =
					partNumbers.length > MAX_NUMBER_ITEMS_SHOW_INDEX
						? MAX_NUMBER_ITEMS_SHOW
						: MAX_NUMBER_ITEMS_SHOW_INDEX;

				return (
					<>
						<Box
							key={mappedOrder.orderId}
							className="recent-orders__order"
							display="flex"
							alignItems="flex-start"
							flexDirection={'row'}
							py={'10px'}
							px={'10px'}
							my={'10px'}
						>
							<Box
								sx={{ opacity: mappedOrder.orderStatus === 'X' ? '60%' : 1 }}
								className="recent-orders__order-gallery"
							>
								<CardImage
									url={`${CDN_BASE_URL}/productos/${partNumbers[0]}/${partNumbers[0]}.jpg`}
									width={133}
									height={133}
									alt={partNumbers[0]}
								/>

								<Box display="flex" alignItems="flex-start" flexDirection={'row'} mt={'5px'}>
									{partNumbers.slice(1, itemsToShow).map((partNumber) => (
										<Box sx={{ mr: '2px' }} key={partNumber}>
											<CardImage
												width={25}
												height={25}
												url={`${CDN_BASE_URL}/productos/${partNumber}/${partNumber}-m.jpg`}
												alt={''}
											/>
										</Box>
									))}
									{partNumbers.length > 6 ? (
										<Typography variant="caption" color={'grey.300'} pt={'5px'}>
											{'+'}
											{partNumbers.length - MAX_NUMBER_ITEMS_SHOW}
										</Typography>
									) : null}
								</Box>
							</Box>
							<Box
								ml={'10px'}
								display={'flex'}
								justifyContent={'flex-start'}
								alignItems={'flex-start'}
								flexDirection={'column'}
							>
								<Box mb={'10px'} className="recent-orders__order-status">
									<MyRecentOrderStatus orderId={mappedOrder.orderId || EMPTY_STRING} />
								</Box>
								<Box>
									<Box className="recent-orders__order-number">
										<TextLine
											orderInfoLabel={HeaderMyRecentOrdersLabels.OrderNumber.t()}
											orderValueLabel={mappedOrder.orderId || EMPTY_STRING}
										/>
									</Box>
									<Box className="recent-orders__order-date">
										{mappedOrder.orderStatus === 'M' ? (
											<Typography
												variant="bodySubText"
												color={'error.main'}
												mb={'10px'}
												component={'p'}
												fontWeight={OpenSansFontWeights.semibold}
											>
												{HeaderMyRecentOrdersLabels.PayPending.t()}
											</Typography>
										) : (
											<RecentOrdersDeliveryDate orderId={mappedOrder.orderId!} />
										)}
									</Box>
									<Box className="recent-orders__order-products">
										<TextLine
											orderInfoLabel={HeaderMyRecentOrdersLabels.NumberOfProducts.t()}
											orderValueLabel={partNumbers.length.toString()}
										/>
									</Box>
									<Box className="recent-orders__order-total">
										<TextLine
											orderInfoLabel={HeaderMyRecentOrdersLabels.TotalPrice.t()}
											orderValueLabel={formatPrice(
												LOCALE_ES_MX,
												mappedOrder.totalProductPriceCurrency!,
												mappedOrder.grandTotal!
											)}
										/>
									</Box>
								</Box>
								<Box mt={'10px'}>
									<Linkable
										href={{
											pathname: routes.OrderDetails.route.t(),
											query: { orderId: mappedOrder?.orderId },
										}}
										sx={extraLinkSX.itemLink}
										className="recent-orders__order-details"
									>
										<Typography variant="bodySubText" color={'secondary.main'}>
											{HeaderMyRecentOrdersLabels.ExtraLinks.SeeDetails.text.t()}
										</Typography>
									</Linkable>
								</Box>
							</Box>
						</Box>
						<Divider />
					</>
				);
			})}
		</>
	);
};
